import type { LibraryStats } from '@zyx/shared';

/** One column of a chart. */
export interface ChartPoint {
  readonly key: string;
  readonly label: string;
  readonly value: number;
}

const DAY_MS = 86_400_000;

/** A `YYYY-MM-DD` as a moment in UTC, so date arithmetic never trips over daylight saving. */
function dayMs(date: string): number {
  return Date.parse(`${date}T00:00:00Z`);
}

/** The date `now` falls on where the viewer is, as `YYYY-MM-DD`. */
export function localDate(now: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** Every year from the first to the last: the x-axis is time, so an empty year shows as a gap. */
export function yearPoints(rows: LibraryStats['releasedByYear']): ReadonlyArray<ChartPoint> {
  const first = rows[0]?.year;
  const last = rows[rows.length - 1]?.year;
  if (first === undefined || last === undefined) {
    return [];
  }
  const counts = new Map(rows.map((row) => [row.year, row.movies]));
  return Array.from({ length: last - first + 1 }, (_unused, index) => {
    const year = first + index;
    return { key: String(year), label: String(year), value: counts.get(year) ?? 0 };
  });
}

/** Every day from the first with a movie to today, so a quiet spell shows as one. */
export function dayPoints(rows: LibraryStats['addedByDay'], today: string): ReadonlyArray<ChartPoint> {
  const first = rows[0]?.date;
  if (first === undefined) {
    return [];
  }
  const counts = new Map(rows.map((row) => [row.date, row.movies]));
  // The service's calendar can be a day ahead of the viewer's.
  const last = Math.max(dayMs(today), dayMs(rows[rows.length - 1].date));
  const start = dayMs(first);
  return Array.from({ length: Math.round((last - start) / DAY_MS) + 1 }, (_unused, index) => {
    const at = new Date(start + index * DAY_MS);
    const date = at.toISOString().slice(0, 10);
    return { key: date, label: at.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' }), value: counts.get(date) ?? 0 };
  });
}

/** Movies added in the `days` days up to and including `today`. */
export function addedInLast(rows: LibraryStats['addedByDay'], days: number, today: string): number {
  const since = dayMs(today) - (days - 1) * DAY_MS;
  return rows.reduce((sum, row) => (dayMs(row.date) >= since ? sum + row.movies : sum), 0);
}
